import { defineStore } from 'pinia'
import type { IOffersParams } from '@ui/interfaces/offers'
import { useAuthStore } from 'pinia-store'
import http from '../helpers/http-call'
import { ENDPOINT_URL, getDataLang, getQuery } from '../helpers/api'

export interface IAdditionalQueryProps {
  showData?: boolean
  category?: string
  headers?: {
    Authorization?: string
  }
  hotel_id?: number
  lang_id?: number
  page_size?: number
  page?: number
  additional_page_id?: number
}
export interface IAdditionalData {
  additional_pages: IAdditional[]
  pagination: {
    page: number
    page_size: number
    total_page: number
    total_data: number
  }
}
export interface IAdditional {
  description: string
  title: string
  name: string
  slug: string
  preview: string
  hotel_id: string
  additional_page_id: string
  operational_hours: string | null
  custom_url: string
  book_now: string
  is_navbar: number
  is_footer: number
  status: number
  lang_id: number
  additional_pages_images: IAdditionalImages[] | null
  additional_pages_package: IAdditionalPackages[] | null
}

export interface IAdditionalImages {
  image: string
  order: number
}

export interface IAdditionalPackages {
  rate: number
  order: number
  image: string
  title: string
  preview: string
  description: string
}

export interface IAdditionalOffers {
  offers: IOffersParams[]
}

export interface IAdditionalState {
  list: IAdditionalData
  data: IAdditionalData
  dataSwiper: IAdditionalData
  loading: boolean
  params: IAdditionalQueryProps
  offers: IOffersParams[]
}

const PARAMS = {
  page: 1,
  page_size: 6,
  category: '',
}

const DATA: IAdditionalData = {
  additional_pages: [],
  pagination: {
    page: 1,
    page_size: 6,
    total_page: 0,
    total_data: 0,
  },
}

export const useAdditionalStore = defineStore('additional', {
  state: (): IAdditionalState => ({
    list: DATA,
    loading: false,
    data: DATA,
    dataSwiper: DATA,
    params: PARAMS,
    offers: [] as IOffersParams[],
  }),
  getters: {
    getData(): IAdditionalState['data'] {
      return getDataLang(this.list, 0, 'additional_pages')
    },
    getDetail(): ((slug: string) => IAdditional | IAdditional[]) | IAdditional[] {
      if (!this.data?.additional_pages)
        return DATA.additional_pages
      const list = getDataLang(this.list, 0, 'additional_pages')
      return (slug: string) =>
        Array.isArray(list) && list.length
          ? (list.find((el: IAdditional) => el.slug === slug) as IAdditional)
          : DATA.additional_pages
    },
    getOffers(): IOffersParams[] {
      return getDataLang(this.offers, 0, 'additional_pages')
    },
  },
  actions: {
    setList(data: IAdditionalData, isData?: boolean) {
      this.list = data
      if (isData)
        this.data = data
    },
    async setData(query: IAdditionalQueryProps) {
      const { category, page_size, page, headers, additional_page_id }: IAdditionalQueryProps = query
      const params: IAdditionalQueryProps = getQuery()

      if (additional_page_id) {
        Object.assign(params, {
          additional_page_id,
        })
      }
      if (category) {
        Object.assign(params, {
          category,
        })
      }
      if (page_size) {
        Object.assign(params, {
          page_size,
        })
      }
      if (page) {
        Object.assign(params, {
          page,
        })
      }
      if (params?.lang_id)
        delete params.lang_id

      let response: any = {
        data: {},
      }
      if (!this.loading) {
        this.loading = true
        response = await http.call({
          url: `${ENDPOINT_URL}/offers`,
          headers,
          params,
          method: 'GET',
          data: null,
        })
      }
      this.params = params

      const useAuth = useAuthStore()
      if (response?.response?.data?.errorCode === 401) {
        const auth = await useAuth?.setData({ showData: true })
        if (auth?.token) {
          response = await http.call({
            url: `${ENDPOINT_URL}/offers`,
            headers: {
              Authorization: `Bearer ${auth.token}`,
            },
            params,
            method: 'GET',
            data: null,
          })
        }
      }

      if (response?.data?.data)
        this.offers = getDataLang(response.data?.data.offers, 0, 'additional_pages')

      setTimeout(() => {
        this.loading = false
      }, 1000)
    },
    setOffers(data: IOffersParams[]) {
      this.offers = data
    },
  },
})
